import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Projects = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const projects = [
    {
      title: "Rural Women Empowerment",
      description: "Empowering rural women through education, skills training, and economic opportunities. Supporting women-led initiatives and promoting gender equality in remote villages.",
      image: "/images/baby warmer and medical equipment,bed given to health care cnetre in villages (2).jpg",
      impact: "Empowering 500+ women",
      gradient: "from-pink-500 to-rose-500"
    },
    {
      title: "Mental Health and Awareness",
      description: "Providing mental health support and awareness programs to rural communities, breaking stigma and offering counseling services to those in need.",
      image: "/images/064.jpeg",
      impact: "Supporting mental wellness",
      gradient: "from-purple-500 to-indigo-500"
    },
    {
      title: "Emergency Relief",
      description: "Rapid response and support during natural disasters and emergencies, including water filter distribution. Providing immediate aid, shelter, food, clean water, and medical assistance to affected communities.",
      image: "/images/064.jpeg",
      impact: "Emergency response ready",
      gradient: "from-red-500 to-orange-500"
    }
  ];

  return (
    <section id="projects" className="relative py-24 bg-gradient-to-br from-black via-neutral-900 to-black overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-black opacity-20"></div>
      <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">
              Our Projects
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-600 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Transforming lives through sustainable development programs in Nepal's most remote villages. Learn more at <a href="https://www.caredevelopment.org/" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors">caredevelopment.org</a>.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="relative group"
            >
              {/* Card */}
              <div className="relative overflow-hidden rounded-2xl bg-neutral-900 border border-neutral-800 group-hover:border-neutral-600 transition-all duration-500">
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${project.gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-500`}></div>
                  
                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    <div className="glass rounded-full px-4 py-2 text-sm font-semibold text-white">
                      {project.impact}
                    </div>
                    <div className="bg-gradient-to-r from-amber-600 to-yellow-600 rounded-full px-4 py-2 text-sm font-semibold text-white">
                      US Supported
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-neutral-300 leading-relaxed mb-6">
                    {project.description}
                  </p>
                  {project.title === "Rural Women Empowerment" && (
                    <Link
                      to="/rural-women-empowerment"
                      className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    >
                      <span>View Success Story</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </Link>
                  )}
                  {project.title === "Mental Health and Awareness" && (
                    <Link
                      to="/mental-health-awareness"
                      className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    >
                      <span>View Annual Report</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </Link>
                  )}
                </div>

                {/* Glow effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${project.gradient} opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500 -z-10`}></div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Help Us Expand Our Impact
          </h3>
          <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
            Your support enables us to reach more communities and create lasting change in Nepal's most remote areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollToSection('donate')} className="relative px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/25">
              <span className="relative z-10">Support Our Projects</span>
            </button>
            <a href="https://www.caredevelopment.org/" target="_blank" rel="noopener noreferrer" className="px-8 py-4 border border-neutral-400 text-neutral-300 font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300 hover:scale-105 inline-block">
              View All Projects
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 